/**
 * Kundenportal - Login-Anfrage (P0, KEIN Login noetig zum Aufrufen) UND, seit Kundenkonto P1
 * (docs/KONZEPT-KUNDENKONTO.md), das echte Dashboard (Vorgangsuebersicht) fuer einen bereits
 * eingeloggten Kunden - DIESELBE Route `/kundenportal` traegt beides:
 *
 *   - Keine (gueltige) Session in localStorage -> das P0-Login-Formular (unveraendert: E-Mail
 *     eingeben -> POST .../request-link -> immer derselbe "Prüfen Sie Ihr Postfach"-Hinweis,
 *     kein Existenz-Orakel). Das ist der ALLERERSTE Aufruf dieser Seite fuer einen Kunden.
 *   - Gueltige Session -> GET .../events (Bearer-Token aus localStorage) und die
 *     Vorgangsuebersicht. Ein 401 (Session widerrufen/abgelaufen - z.B. nach "alle Geräte
 *     abmelden", P4) raeumt die Session auf und faellt automatisch zurueck auf das Formular.
 *
 * Warum EINE Seite statt zwei getrennter Routen: der Login-Callback (kundenportal-anmelden.tsx)
 * verlinkt nach dem Einloggen bereits auf `/kundenportal` - ohne Umbauten braeuchte ein Kunde
 * sonst zwei URLs im Kopf. Die Fallunterscheidung selbst ist rein clientseitig (kein Extra-
 * Server-Redirect noetig, Muster wie authProvider.ts fuer den Mitarbeiter-Login).
 */
import { useEffect, useState } from "react";
import { Button, Icon, Input, Logo, useBestaetigung, ButtonLink } from "../ui/components";
import { anlassAnzeige, eventStatusMeta, money, formatDate, PILL_CLASSES } from "../ui/format";
import { getCustomerSessionToken, customerPortalFetch, customerPortalApiError, clearCustomerSession, CustomerSessionExpiredError } from "../lib/customer-portal-client";
import { BetriebsKopf, betriebsName, useBetriebsMarke } from "../ui/BetriebsKopf";
import { zahlungsartLabel, deckteGesamtbetrag } from "../lib/zahlungsart-label";
import { FesteLeiste, FESTE_LEISTE_PLATZ } from "../ui/AngebotsseiteV2";

type FormPhase = "form" | "submitting" | "sent";
type Phase = FormPhase | "loading" | "dashboard";

interface CustomerPortalEventSummary {
  id: string;
  number: string | null;
  status: string;
  eventDate: string | null;
  dateEnd: string | null;
  guestCount: number | null;
  occasion: string | null;
  amountTotalCents: number | null;
  createdAt: string;
  chosenOption: { id: string; label: string; title: string | null; amountTotalCents: number } | null;
}

/** P2: invoiceType-Label (invoices.invoice_type) - eigene, kleine Uebersetzung, kein Aequivalent
 *  in ui/format.ts (PAYMENT_TYPE_LABEL dort deckt payments.payment_type ab, ein anderes Feld). */
const INVOICE_TYPE_LABEL: Record<string, string> = {
  invoice: "Rechnung",
  deposit: "Anzahlungsrechnung",
  final: "Schlussrechnung",
  credit: "Gutschrift",
};

/** P2: invoices.status - eigene Uebersetzung (PAYMENT_STATUS in ui/format.ts deckt payments.status
 *  ab, ein anderer Zustandsraum: draft/open/paid/voided/failed/voiding statt pending/processing/…).
 *  'draft' kommt hier nie an (die Route liefert nur sent_at IS NOT NULL-Zeilen, s. sql/347). */
const INVOICE_STATUS_LABEL: Record<string, { label: string; tone: keyof typeof PILL_CLASSES }> = {
  open: { label: "Offen", tone: "warn" },
  paid: { label: "Bezahlt", tone: "success" },
  voided: { label: "Storniert", tone: "neutral" },
  voiding: { label: "Wird storniert", tone: "neutral" },
  failed: { label: "Fehlgeschlagen", tone: "danger" },
};

interface CustomerPortalInvoice {
  id: string;
  eventId: string;
  eventOccasion: string | null;
  invoiceType: string;
  status: string;
  invoiceNumber: string | null;
  amountGrossCents: number | null;
  sentAt: string | null;
  createdAt: string;
  pdfUrl: string | null;
}

interface CustomerPortalPayment {
  id: string;
  eventId: string;
  paymentType: string;
  status: string;
  amountCents: number;
  checkoutUrl: string | null;
  dueDate: string | null;
  paidAt: string | null;
  createdAt: string;
}

export default function KundenportalPage() {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>(getCustomerSessionToken() ? "loading" : "form");
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<CustomerPortalEventSummary[]>([]);

  useEffect(() => {
    if (phase !== "loading") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await customerPortalFetch("/api/public/customer-portal/events");
        if (!res.ok) throw await customerPortalApiError(res);
        const body = (await res.json()) as { data: { events: CustomerPortalEventSummary[] } };
        if (cancelled) return;
        setEvents(body.data.events);
        setPhase("dashboard");
      } catch (err) {
        if (cancelled) return;
        // CustomerSessionExpiredError raeumt localStorage bereits selbst auf (customer-portal-
        // client.ts) - hier nur noch zurueck auf das Login-Formular, kein Fehlertext (der Kunde
        // sieht schlicht wieder die Startseite, wie beim allerersten Besuch).
        if (!(err instanceof CustomerSessionExpiredError)) {
          setError(err instanceof Error ? err.message : String(err));
        }
        setPhase("form");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  async function submit() {
    if (!email.trim()) return;
    setPhase("submitting");
    setError(null);
    try {
      const res = await fetch("/api/public/customer-portal/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Bewusst KEINE Fallunterscheidung nach res.ok fuer die Anzeige: 200, 422 (ungueltiges
      // Format) und ein Netzwerkfehler fuehren alle zum selben "Prüfen Sie Ihr Postfach"-Hinweis,
      // AUSSER echten 4xx/5xx-Fehlern, die die Route selbst NIE aus einem Nicht-Treffer erzeugt
      // (429 rate_limited, 400 tenant_unresolved) - die zeigen wir ehrlich als Fehler.
      if (res.status === 429) throw new Error("Zu viele Anfragen. Bitte warten Sie kurz und versuchen Sie es erneut.");
      if (res.status === 400) throw new Error("Diese Seite ist unter dieser Adresse nicht erreichbar.");
      setPhase("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("form");
    }
  }

  if (phase === "loading") {
    return (
      <div className="mx-auto min-h-screen max-w-3xl px-5 py-12 text-center">
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Wird geladen …</p>
      </div>
    );
  }

  if (phase === "dashboard") {
    // Session kann WAEHREND des Browsens ablaufen/widerrufen werden (z.B. "alle Geräte
    // abmelden" auf einem anderen Geraet, P4) - der Rechnungen-Tab laedt eigenstaendig nach und
    // meldet einen 401 ueber denselben Rueckweg zurueck auf das Login-Formular.
    return <KundenportalDashboard events={events} onSessionExpired={() => setPhase("form")} />;
  }

  return (
    <KundenportalAnmeldung
      email={email}
      onEmail={setEmail}
      onSubmit={() => void submit()}
      gesendet={phase === "sent"}
      sendet={phase === "submitting"}
      error={error}
    />
  );
}

/**
 * Anmeldeseite des Kundenportals (Design-Durchsicht, Antoine 26.09.2026: „Das muss besser
 * aussehen.“): Logo + Betriebsname groß und ruhig, Begrüßung in Newsreader wie die Angebotsseite,
 * ein Satz, was man hier findet, E-Mail-Feld + primärer Knopf, Hinweis, was danach passiert.
 * Eigenes Layout statt `GastRahmen`, weil die Marke hier groß über der Karte steht (kein Kopfbalken
 * über einer leeren Fläche). Antoine 26.09.2026: Variante A (ohne Kopfbalken) ist Standard; unten
 * Datenschutz- und Impressum-Link, sofern der Betrieb sie hinterlegt hat.
 * Logik unverändert — alles kommt als Props aus KundenportalPage.
 */
function KundenportalAnmeldung({
  email,
  onEmail,
  onSubmit,
  gesendet,
  sendet,
  error,
}: {
  email: string;
  onEmail: (v: string) => void;
  onSubmit: () => void;
  gesendet: boolean;
  sendet: boolean;
  error: string | null;
}) {
  const marke = useBetriebsMarke();
  const name = betriebsName(marke?.name);
  const markenZeichen = marke?.logo_url ? (
    <img src={marke.logo_url} alt={name} className="mx-auto h-14 max-w-[200px] object-contain md:h-16" />
  ) : (
    <Logo size={56} radiusClassName="rounded-[14px]" textClassName="text-[28px]" buchstabe={(name.charAt(0) || "M").toUpperCase()} className="mx-auto" />
  );

  const inhalt = gesendet ? (
    <div className="text-center" role="status">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-container-high text-on-surface-variant dark:bg-white/10 dark:text-secondary-fixed-dim">
        <Icon name="mark_email_read" className="text-[28px]" />
      </div>
      <h1 className="font-serif text-[28px] font-medium leading-tight text-on-surface dark:text-inverse-on-surface">Prüfen Sie Ihr Postfach</h1>
      <p className="mt-3 font-body-md text-body-md text-secondary dark:text-secondary-fixed-dim">
        Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen Login-Link geschickt. Der Link ist 20 Minuten gültig.
      </p>
      <p className="mt-4 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Keine E-Mail da? Schauen Sie bitte auch im Spam-Ordner nach.</p>
    </div>
  ) : (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim() && !sendet) onSubmit();
      }}
    >
      <p className="text-center font-label-md text-label-md uppercase tracking-[0.22em] text-on-surface-variant dark:text-secondary-fixed-dim">Kundenportal</p>
      <h1 className="mt-2 text-center font-serif text-[30px] font-medium leading-[1.15] text-on-surface [text-wrap:balance] dark:text-inverse-on-surface md:text-[34px]">
        Willkommen bei <span className="block">{name}</span>
      </h1>
      <p className="mx-auto mt-3 max-w-sm text-center font-body-md text-body-md text-secondary dark:text-secondary-fixed-dim">
        Hier finden Sie Ihre Angebote, Buchungen und Rechnungen an einem Ort.
      </p>
      <label htmlFor="kundenportal-email" className="mt-7 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
        E-Mail-Adresse
      </label>
      <Input
        id="kundenportal-email"
        className="mt-1.5 w-full !py-3.5 text-[16px]"
        type="email"
        autoComplete="email"
        inputMode="email"
        placeholder="name@beispiel.de"
        value={email}
        onChange={(e) => onEmail(e.target.value)}
      />
      {error && <p className="mt-4 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{error}</p>}
      <Button type="submit" className="mt-5 w-full !py-4 text-[15px]" disabled={!email.trim() || sendet}>
        {sendet ? "Wird gesendet …" : "Anmeldelink senden"}
      </Button>
      <div className="mt-5 flex gap-3 rounded-xl bg-surface-container-low px-4 py-3.5 dark:bg-white/5">
        <Icon name="mail" className="mt-0.5 shrink-0 text-[20px] text-secondary dark:text-secondary-fixed-dim" />
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Sie erhalten eine E-Mail mit einem Anmeldelink. Ein Klick darauf genügt – ganz ohne Passwort. Der Link ist 20 Minuten gültig.
        </p>
      </div>
    </form>
  );

  const linkKlasse = "underline-offset-2 hover:text-on-surface hover:underline dark:hover:text-inverse-on-surface";
  const fuss = (
    <div className="mt-8 text-center font-body-sm text-[12px] text-on-surface-variant/80 dark:text-secondary-fixed-dim">
      <p className="flex items-center justify-center gap-1.5">
        <Icon name="lock" className="text-[14px]" />
        Sichere Anmeldung für Gäste von {name}
      </p>
      {(marke?.privacy_url || marke?.impressum_url) && (
        <nav aria-label="Rechtliches" className="mt-2 flex justify-center gap-4">
          {marke.privacy_url && (
            <a href={marke.privacy_url} target="_blank" rel="noopener noreferrer" className={linkKlasse}>
              Datenschutz
            </a>
          )}
          {marke.impressum_url && (
            <a href={marke.impressum_url} target="_blank" rel="noopener noreferrer" className={linkKlasse}>
              Impressum
            </a>
          )}
        </nav>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background px-4 dark:bg-night-canvas">
      <main className="mx-auto w-full max-w-[440px] flex-1 pb-10 pt-12 md:pt-[12vh]">
        <div className="mb-7 text-center">{markenZeichen}</div>
        <div className="rounded-2xl border border-[#EFE8DA] bg-surface-container-lowest px-6 py-8 shadow-[0_8px_18px_-10px_rgba(45,38,26,.14)] dark:border-white/10 dark:bg-night-card md:px-9 md:py-10">
          {inhalt}
        </div>
        {fuss}
      </main>
    </div>
  );
}

type DashboardTab = "vorgaenge" | "rechnungen";

/** Dashboard-Rahmen (P1 Vorgangsuebersicht + P2 Rechnungen-Tab + P4 "alle Geräte abmelden") mit
 *  Reiter-Umschalter. */
function KundenportalDashboard({ events, onSessionExpired }: { events: CustomerPortalEventSummary[]; onSessionExpired: () => void }) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const [tab, setTab] = useState<DashboardTab>("vorgaenge");
  const [loggingOut, setLoggingOut] = useState(false);

  /**
   * P4: der Backend-Endpunkt (POST .../logout-all) besteht bereits seit P0 - hier nur die
   * UI-Anbindung. Widerruft ALLE Sessions dieses Kunden, einschliesslich der aktuellen (Muster
   * wie beim Mitarbeiter-Logout: "auf allen Geraeten" trifft auch das aufrufende Geraet).
   * Best-effort: selbst wenn der Aufruf scheitert (Session bereits abgelaufen o.ae.), wird lokal
   * trotzdem ausgeloggt - das Ergebnis fuer den Kunden ("ich bin jetzt draussen") stimmt so oder so.
   */
  async function handleLogoutAll() {
    if (
      !(await bestaetigen({
        titel: "Auf allen Geräten abmelden?",
        text: "Sie müssen sich danach überall neu einloggen.",
        bestaetigen: "Abmelden",
      }))
    )
      return;
    setLoggingOut(true);
    try {
      await customerPortalFetch("/api/public/customer-portal/logout-all", { method: "POST" });
    } catch {
      /* s.o. - lokal wird trotzdem ausgeloggt. */
    }
    clearCustomerSession();
    onSessionExpired();
  }

  return (
    <div className="min-h-screen bg-background">
    <BetriebsKopf />
    {bestaetigungsDialog}
    <div className="mx-auto max-w-3xl px-5 py-12">
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Mein Kundenkonto</h1>
        <button
          type="button"
          onClick={() => void handleLogoutAll()}
          disabled={loggingOut}
          className="inline-flex items-center gap-1.5 font-label-md text-label-md text-secondary hover:text-on-surface disabled:opacity-50 dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
        >
          <Icon name="logout" className="text-[18px]" />
          Alle Geräte abmelden
        </button>
      </div>
      <div className="mt-5 flex gap-2 border-b border-outline-variant dark:border-white/10">
        {(
          [
            ["vorgaenge", "Meine Vorgänge"],
            ["rechnungen", "Rechnungen"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 font-label-md text-label-md transition ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-secondary hover:text-on-surface dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "vorgaenge" ? <KundenportalVorgaengeTab events={events} /> : <KundenportalRechnungenTab onSessionExpired={onSessionExpired} events={events} />}
    </div>
    </div>
  );
}

/** Vorgangsuebersicht (P1) - eine Karte je Vorgang, klickbar zur Detailseite. */
function KundenportalVorgaengeTab({ events }: { events: CustomerPortalEventSummary[] }) {
  if (events.length === 0) {
    return <p className="mt-6 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Noch keine Vorgänge vorhanden.</p>;
  }
  return (
    <div className="mt-6 flex flex-col gap-3">
      {events.map((e) => {
        const meta = eventStatusMeta(e.status);
        return (
          <a
            key={e.id}
            href={`/kundenportal/vorgang/${e.id}`}
            className="flex items-center justify-between gap-4 rounded-xl border border-outline-variant bg-surface px-5 py-4 transition hover:border-outline dark:border-white/10 dark:bg-white/[0.03]"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-title-md text-title-md text-on-surface dark:text-inverse-on-surface">
                  {anlassAnzeige(e.occasion) || "Vorgang"}
                </span>
                <span className={`rounded-md px-1.5 py-0.5 font-label-sm text-label-sm ${PILL_CLASSES[meta.tone]}`}>{meta.label}</span>
              </div>
              <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {[
                  formatDate(e.eventDate),
                  e.guestCount != null ? `${e.guestCount} Gäste` : null,
                  // Kein Buchstaben-Fallback mehr (Antoine, 11.09.2026) — ohne eigenen Titel zeigt
                  // die Zeile den Preis der gewählten Variante statt "Variante A".
                  e.chosenOption?.title || (e.chosenOption ? money(e.chosenOption.amountTotalCents) : null),
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {(e.chosenOption?.amountTotalCents ?? e.amountTotalCents) != null && (
                <span className="font-title-sm text-title-sm text-on-surface dark:text-inverse-on-surface">
                  {money(e.chosenOption?.amountTotalCents ?? e.amountTotalCents)}
                </span>
              )}
              <Icon name="chevron_right" className="text-secondary dark:text-secondary-fixed-dim" />
            </div>
          </a>
        );
      })}
    </div>
  );
}

/**
 * Rechnungen + Zahlungen (P2) - laedt eigenstaendig (Reiter-Wechsel, kein Bestandteil des
 * anfaenglichen Dashboard-Ladens). Zwei Bloecke: offene Zahlungen ZUERST (Anzahlung/Restzahlung
 * mit gueltigem Stripe-Link, falls vorhanden - Auftrag P2), darunter die Rechnungsliste.
 */
function KundenportalRechnungenTab({ onSessionExpired, events }: { onSessionExpired: () => void; events: CustomerPortalEventSummary[] }) {
  const [state, setState] = useState<{ phase: "loading" | "ok" | "error" } & { invoices: CustomerPortalInvoice[]; payments: CustomerPortalPayment[] }>({
    phase: "loading",
    invoices: [],
    payments: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await customerPortalFetch("/api/public/customer-portal/invoices");
        if (!res.ok) throw await customerPortalApiError(res);
        const body = (await res.json()) as { data: { invoices: CustomerPortalInvoice[]; payments: CustomerPortalPayment[] } };
        if (cancelled) return;
        setState({ phase: "ok", invoices: body.data.invoices, payments: body.data.payments });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof CustomerSessionExpiredError) {
          onSessionExpired();
          return;
        }
        setState((s) => ({ ...s, phase: "error" }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSessionExpired ist eine stabile Closure aus dem Elternteil (setPhase), kein Re-Fetch-Ausloeser.
  }, []);

  if (state.phase === "loading") {
    return <p className="mt-6 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Wird geladen …</p>;
  }
  if (state.phase === "error") {
    return <p className="mt-6 font-body-sm text-body-sm text-error">Rechnungen konnten nicht geladen werden. Bitte später erneut versuchen.</p>;
  }

  const offenePayments = state.payments.filter((p) => (p.status === "pending" || p.status === "processing") && p.checkoutUrl);
  // Antoine, 14.10.2026 (Mockup „Zahlleiste“): bei GENAU einer offenen Zahlung wandert der Knopf in
  // eine feste Leiste unten. Bei mehreren bleibt je Karte ein eigener Knopf.
  const einzigeZahlung = offenePayments.length === 1 ? offenePayments[0] : null;
  // 100 % bei Buchung: deckt die Zahlung die Gesamtsumme des Vorgangs, heisst sie hier "Zahlung" statt "Anzahlung".
  const zahlungsName = (p: CustomerPortalPayment) =>
    zahlungsartLabel(p.paymentType, p.amountCents, events.find((e) => e.id === p.eventId)?.amountTotalCents) || p.paymentType;
  const zahlenKnopf = (p: CustomerPortalPayment) => (
    <ButtonLink href={p.checkoutUrl ?? "#"} target="_blank" rel="noreferrer" className="!py-2 !px-4">
      <Icon name="credit_card" className="text-[18px]" />
      Jetzt bezahlen
    </ButtonLink>
  );
  const faellig = (p: CustomerPortalPayment) => (p.dueDate ? `Fällig am ${formatDate(p.dueDate)}` : "Fälligkeit offen");

  return (
    <div className={`mt-6 flex flex-col gap-8 ${einzigeZahlung ? FESTE_LEISTE_PLATZ : ""}`}>
      {einzigeZahlung && (
        <FesteLeiste
          testId="kundenportal-zahlleiste"
          info={`${zahlungsName(einzigeZahlung)} · ${money(einzigeZahlung.amountCents)}`}
          hinweis={faellig(einzigeZahlung)}
        >
          {zahlenKnopf(einzigeZahlung)}
        </FesteLeiste>
      )}
      {offenePayments.length > 0 && (
        <div>
          <h2 className="font-title-md text-title-md text-on-surface dark:text-inverse-on-surface">Offene Zahlungen</h2>
          <div className="mt-3 flex flex-col gap-3">
            {offenePayments.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-outline-variant bg-surface px-5 py-4 dark:border-white/10 dark:bg-white/[0.03]"
              >
                <div>
                  <div className="font-title-sm text-title-sm text-on-surface dark:text-inverse-on-surface">{zahlungsName(p)}</div>
                  <div className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {faellig(p)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-title-sm text-title-sm text-on-surface dark:text-inverse-on-surface">{money(p.amountCents)}</span>
                  {!einzigeZahlung && zahlenKnopf(p)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-title-md text-title-md text-on-surface dark:text-inverse-on-surface">Rechnungen</h2>
        {state.invoices.length === 0 ? (
          <p className="mt-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Noch keine Rechnungen vorhanden.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-outline-variant dark:border-white/10">
            <table className="w-full text-left">
              <tbody>
                {state.invoices.map((inv) => {
                  // Abrechnung Stufe 3: eine Schlussrechnung mit Minusbetrag ist für den Gast nichts Offenes.
                  const statusMeta = inv.status === "open" && (inv.amountGrossCents ?? 0) < 0 && inv.invoiceType !== "credit" ? { label: "Zu Ihren Gunsten", tone: "info" as const } : (INVOICE_STATUS_LABEL[inv.status] ?? { label: inv.status, tone: "neutral" as const });
                  return (
                    <tr key={inv.id} className="border-b border-outline-variant last:border-0 dark:border-white/10">
                      <td className="px-4 py-3 align-top">
                        <div className="font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">
                          {inv.invoiceType === "deposit" && deckteGesamtbetrag(inv.amountGrossCents, events.find((e) => e.id === inv.eventId)?.amountTotalCents) ? "Rechnung" : INVOICE_TYPE_LABEL[inv.invoiceType] ?? inv.invoiceType}
                          {inv.invoiceNumber ? ` ${inv.invoiceNumber}` : ""}
                        </div>
                        {inv.eventOccasion && (
                          <div className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{inv.eventOccasion}</div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top">
                        <span className={`rounded-md px-1.5 py-0.5 font-label-sm text-label-sm ${PILL_CLASSES[statusMeta.tone]}`}>{statusMeta.label}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right align-top font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">
                        {money(inv.amountGrossCents)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right align-top">
                        {inv.pdfUrl && (
                          <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-label-md text-label-md text-on-surface underline underline-offset-2">
                            <Icon name="picture_as_pdf" className="text-[18px]" />
                            PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
